// components/PlanBadge.tsx
import React from 'react';
import './planBadge.css';

interface PlanBadgeProps {
  planName: string | undefined;
  planCost?: string;
  status: 'active' | 'trialing' | 'expired' | undefined | string;
  trialDaysRemaining?: number;
  planDaysRemaining?: number;
}

export const PlanBadge: React.FC<PlanBadgeProps> = ({
  planName,
  planCost,
  status,
  trialDaysRemaining,
  planDaysRemaining,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'active':
        return {
          containerClass: 'planBadgeContainer planBadgeActive',
          icon: (
            <svg
              className="planBadgeIcon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          ),
        };
      case 'trialing':
        return {
          containerClass: 'planBadgeContainer planBadgeTrial',
          icon: (
            <svg
              className="planBadgeIcon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          ),
        };
      case 'expired':
        return {
          containerClass: 'planBadgeContainer planBadgeExpired',
          icon: (
            <svg
              className="planBadgeIcon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          ),
        };
      default:
        return {
          containerClass: 'planBadgeContainer',
          icon: null,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={config.containerClass}>
      <div className="planBadgeLeft">
        {config.icon}
        <span className="planBadgeName">{planName}</span>
        {status === 'trialing' && (
          <span className="planBadgeTrialTag">Trial</span>
        )}
        {status === 'expired' && (
          <span className="planBadgeExpiredTag">Expired</span>
        )}
      </div>

      {status !== 'expired' && <div className="planBadgeDivider"></div>}

      {status === 'active' && planCost && (
        <div className="planBadgeRight">
          <span className="planBadgeCost">{planCost}</span>
          <span className="planBadgePeriod">/month</span>
          {planDaysRemaining !== undefined && (
            <span className="planBadgeDaysRemaining">
              • {planDaysRemaining} {planDaysRemaining === 1 ? 'day' : 'days'}{' '}
              left
            </span>
          )}
        </div>
      )}
      {status === 'trialing' && trialDaysRemaining !== undefined && (
        <div className="planBadgeRight">
          <span className="planBadgeCost">{trialDaysRemaining}</span>
          <span className="planBadgePeriod">
            {trialDaysRemaining === 1 ? 'day left' : 'days left'}
          </span>
        </div>
      )}
    </div>
  );
};

// Usage Examples:
/*
// Active Plan
<PlanBadge 
  planName="Growth+ Plan" 
  planCost="$299" 
  status="active" 
/>

// Trial Plan
<PlanBadge 
  planName="Basic Plan" 
  status="trial" 
  trialDaysRemaining={7} 
/>

// Expired Plan
<PlanBadge 
  planName="Growth+ Plan" 
  status="expired" 
/>
*/
